import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { FiLayers, FiMapPin, FiUser } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import MessageBox from '../../components/MessageBox';
import AssetService from '../../services/asset.service';
import BillService from '../../services/bill.service';
import ContractService from '../../services/contract.service';
import AssetSaleService from '../../services/assetSale.service';
import { getError, showCurrency } from '../../utils';
import AssetContracts from '../Assets/AssetContracts';
import AssetInfo from '../Assets/AssetInfo';
import HistoryModal from '../../components/HistoryModal';
import AssetBills from '../Assets/AssetBills';
import { splitBillsByType } from '../Assets/billGrouping';
import AssetSales from '../Assets/AssetSales';
import { AssetDeletionModal } from '../../components/CascadeDeletionModal';
import SingleObjectDocumentManager from '../../components/SingleObjectDocumentManager/SingleObjectDocumentManager';
import GenerateContractModal from '../../components/GenerateContractModal/GenerateContractModal';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import { EntityList, DetailHeader, DetailBreadcrumb, TabNav, Button } from '../../components/EntityList';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return { ...state, asset: action.payload, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	default:
		return state;
	}
};

const emptyTabState = { rows: [], loading: true, error: '' };

const hasValue = (value) => value !== undefined && value !== null && value !== '';
const hasMoneyValue = (value) => hasValue(value) && Number(value) !== 0;

function formatAmount(asset, value) {
	if (!hasMoneyValue(value)) {
		return null;
	}
	const amount = Number(value);
	if (Number.isNaN(amount)) {
		return null;
	}
	const currency = showCurrency(asset.currency) || asset.currency || '';
	return `${currency} ${amount.toLocaleString('es-UY')}`.trim();
}

const TAB_SLUGS = ['info', 'bills-eventual', 'bills-periodic', 'contracts', 'sales', 'documents'];

// The single 'bills' tab was split in two. Keep the old slug resolving so
// existing bookmarks (and NewBillsNew's hardcoded return path) land on a real
// tab instead of silently dropping the user on Detalles.
const LEGACY_TAB_REDIRECTS = { bills: 'bills-eventual' };

export default function AssetDetails() {
	const { t } = useLabels();
	const params = useParams();
	const navigate = useNavigate();
	const { canEdit, canCreate, canDelete } = usePermissions();
	const { id, tab } = params;

	const [{ asset, loading, error }, dispatch] = useReducer(reducer, {
		asset: {},
		loading: true,
		error: '',
	});

	const [showHistoryLog, setShowHistoryLog] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showGeneratePdf, setShowGeneratePdf] = useState(false);

	const [billsState, setBillsState] = useState(emptyTabState);
	const [contractsState, setContractsState] = useState(emptyTabState);
	const [salesState, setSalesState] = useState(emptyTabState);

	const openEditorPage = () => navigate(`/AdminScreen/assets/editAsset/${id}`);

	const fetchData = async () => {
		dispatch({ type: 'FETCH_REQUEST' });
		try {
			const result = await AssetService.getDetails(id);
			dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
		} catch (error) {
			dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
			toast.error(t('msg.listLoadFailed'));
		}
	};

	const fetchBills = useCallback(async () => {
		setBillsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await BillService.listBillsForAsset(id);
			setBillsState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setBillsState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	// Bills are fetched once here and split between the two tabs, so a delete on
	// one tab can't leave the other stale.
	const billsByType = useMemo(() => splitBillsByType(billsState.rows), [billsState.rows]);

	const fetchContracts = useCallback(async () => {
		setContractsState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await ContractService.getContractsForProperty(id);
			setContractsState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setContractsState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	const fetchSales = useCallback(async () => {
		setSalesState((prev) => ({ ...prev, loading: true }));
		try {
			const { data } = await AssetSaleService.getSalesForProperty(id);
			setSalesState({ rows: data, loading: false, error: '' });
		} catch (error) {
			setSalesState({ rows: [], loading: false, error: getError(error) });
		}
	}, [id]);

	useEffect(() => {
		fetchData();
		fetchBills();
		fetchContracts();
		fetchSales();
	}, [id]);

	function deleteAssetHandler() {
		setShowDeleteModal(true);
	}


	function handleCloseDeleteModal() {
		setShowDeleteModal(false);
	}

	// The effect below already rewrites the URL whenever tab !== currentTab, so
	// the legacy redirect falls out of the existing machinery.
	const currentTab = TAB_SLUGS.includes(tab) ? tab : (LEGACY_TAB_REDIRECTS[tab] ?? 'info');

	useEffect(() => {
		if (!loading && tab !== currentTab) {
			navigate(`/AdminScreen/assets/asset/${id}/${currentTab}`, { replace: true });
		}
	}, [loading, tab, currentTab, id]);

	const tabs = [
		{ id: 'info', label: t('title.details') },
		{ id: 'bills-eventual', label: t('title.billsEventual'), count: billsByType.eventual.length },
		{ id: 'bills-periodic', label: t('title.billsPeriodic'), count: billsByType.periodic.length },
		{ id: 'contracts', label: t('entity.contract.plural'), count: contractsState.rows.length },
		{ id: 'sales', label: t('entity.assetSale.plural'), count: salesState.rows.length },
		{ id: 'documents', label: t('title.documents') },
	];

	const subline = [
		asset.owner?.name && { label: t('entity.assetOwner'), icon: <FiUser />, value: asset.owner.name, strong: true },
		hasMoneyValue(asset.price) && {
			label: t('field.price'),
			icon: <FiLayers />,
			value: <span className='tw:font-medium tw:text-cat-4'>{formatAmount(asset, asset.price)}</span>,
		},
		asset.ubication && { label: t('field.ubicationPlain'), icon: <FiMapPin />, value: asset.ubication },
		asset.lot && { label: t('field.lotUpper'), value: <span className='tw:font-mono'>p. {asset.lot}</span> },
	].filter(Boolean);

	return loading ? (
		<EntityList>
			<DetailPageLoader entityLabel={t('entity.asset')} />
		</EntityList>
	) : error ? (
		<MessageBox variant='danger'>{error}</MessageBox>
	) : (
		<EntityList>
			<DetailBreadcrumb
				parentLabel={t('entity.asset.plural')}
				parentTo='/AdminScreen/assets'
				current={asset.customId}
			/>

			<DetailHeader
				name={asset.name || ''}
				kicker={`${t('entity.assetSentence')} · ${asset.customId || ''}`}
				// `sold` replaces the spec's "multiplesPadrones" pill — sold status is the
				// more meaningful real-world signal for this field's second badge.
				statusLabel={asset.sold ? t('field.sold') : asset.type}
				statusTone={asset.sold ? 'danger' : 'neutral'}
				subline={subline}
				avatar={
					<span className='tw:flex tw:h-[72px] tw:w-[72px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[14px] tw:bg-cat-4 tw:text-white tw:shadow-sm'>
						<FiLayers size={28} />
					</span>
				}
				actions={
					<>
						<Button onClick={() => setShowHistoryLog(true)}>{t('title.changeHistory')}</Button>
						{canCreate() && (
							<Button onClick={() => setShowGeneratePdf(true)}>{t('action.generateContract')}</Button>
						)}
						{canEdit() && (
							<Button variant='primary' onClick={openEditorPage}>
								{t('action.edit')}
							</Button>
						)}
						{canDelete() && <Button onClick={deleteAssetHandler}>{t('action.delete')}</Button>}
					</>
				}
			/>

			<div className='tw:mt-5'>
				<TabNav
					tabs={tabs}
					current={currentTab}
					onChange={(nextTab) => navigate(`/AdminScreen/assets/asset/${id}/${nextTab}`)}
				/>
			</div>

			<div className='tw:mt-6'>
				{currentTab === 'info' && <AssetInfo asset={asset} />}
				{currentTab === 'bills-eventual' && <AssetBills asset={asset} billType='eventual' {...billsState} onRefetch={fetchBills} />}
				{currentTab === 'bills-periodic' && <AssetBills asset={asset} billType='periodic' {...billsState} onRefetch={fetchBills} />}
				{currentTab === 'contracts' && <AssetContracts assetId={id} {...contractsState} onRefetch={fetchContracts} />}
				{currentTab === 'sales' && <AssetSales assetId={id} {...salesState} onRefetch={fetchSales} />}
				{currentTab === 'documents' && (
					<SingleObjectDocumentManager objectId={id} objectType='escritura' documentType='escritura' />
				)}
			</div>

			<HistoryModal model='asset' show={showHistoryLog} handleClose={() => setShowHistoryLog(false)} id={id} isHistory={true} />

			{showGeneratePdf && (
				<GenerateContractModal
					show={showGeneratePdf}
					onHide={() => setShowGeneratePdf(false)}
					entityType='asset'
					entityId={id}
				/>
			)}

			<AssetDeletionModal
				show={showDeleteModal}
				record={{ _id: id }}
				onClose={handleCloseDeleteModal}
				onDeleted={() => navigate('/AdminScreen/assets')}
			/>
		</EntityList>
	);
}
